import './CleanSlate.css';
import happy from '../../happy.svg';
import { Column, Font, PaletteBorderStyle, Row } from './DesignSystem';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useWindows, type WindowAction, type WindowID } from './Windows';
import {
  ALL_FIDGET_MODULES,
  useConversation,
  type FidgetModule,
} from '@/services/ConversationService';
import { EnableDevJSONImport } from '@/services/ServiceConstants';
import { Draggable } from './Palette';
import { Spacer, useColorPreview } from './ColorPreview';
import { useTypographyPreview } from './TypographyPreview';
import { useScreenPreview } from './ScreenPreview';
import { useComponentsPreview } from './ComponentsPreview';
import { LivePreview } from './LivePreview';

export type SlateState =
  | { type: 'loading'; loadingText: string }
  | {
      type: 'fidget';
      title: string;
      id: Exclude<WindowID, 'codi'>;
      showCodi?: boolean;
    }
  | { type: 'live-preview' };

export type CodiTipProps = {
  text: string;
  height: number;
  width: number;
  active: boolean;
  onClick: () => void;
};
export function CodiTip({
  // Codi Tips
  text,
  height,
  width,
  active,
  onClick,
}: CodiTipProps) {
  const uppercasedFirstText = text.charAt(0).toUpperCase() + text.slice(1);
  return (
    <Row
      style={{
        ...PaletteBorderStyle,
        width: width - 16,
        height: height - 16,
        backgroundColor: active ? 'white' : '#ffffff80',
        backdropFilter: 'blur(2px)',
        padding: 6,
        alignItems: 'center',
        cursor: 'pointer',
      }}
      onClick={onClick}
    >
      <Font
        kind="font-palette-header"
        style={{ display: 'block', marginInlineStart: 8 }}
      >
        {uppercasedFirstText}
      </Font>
    </Row>
  );
}
export function CodiTipPalette({
  // List of Codi Tips
  activeFidgetModuleIndex,
  fidgetModules,
  onFidgetClick,
}: {
  activeFidgetModuleIndex: number;
  fidgetModules: FidgetModule[];
  onFidgetClick: (_: number) => void;
}) {
  const { state: windowState } = useWindows();

  const width = windowState.windows['codi-tip'].size?.width;
  if (width === undefined) {
    return <></>;
  }

  const hiddenModules: FidgetModule[] = ['components', 'screens'];

  return (
    <Draggable windowID="codi-tip">
      <Column style={{ width, gap: 16 }}>
        {fidgetModules.map((s, i) => {
          if (hiddenModules.includes(s)) {
            return;
          }

          const height = 52;
          const isActive = i === activeFidgetModuleIndex;
          return (
            <div key={s} style={{ display: 'contents' }}>
              <CodiTip
                text={s}
                active={isActive}
                width={width}
                height={height}
                onClick={() => {
                  onFidgetClick(i);
                }}
              />
            </div>
          );
        })}
      </Column>
    </Draggable>
  );
}

export const EnableCleanSlate = true;
export const SlateIconWidth = 244;
export const EnableLivePreview = true;
export { EnableDevJSONImport };

type CleanSlateState =
  | // Wait for the initial prompt
  { type: 'initial' }
  // Streaming the responses
  | {
      type: 'loading';
      preparingLivePreview?: boolean;
      showingLivePreview?: boolean;
    }
  | { type: 'complete' };

function WindowIDForFidgetModule(fidgetModule: FidgetModule): WindowID {
  switch (fidgetModule) {
    case 'colors':
      return 'colors';
    case 'typography':
      return 'typography';
    case 'screens':
      return 'screens';
    case 'components':
      return 'components';
    case 'connect':
      return 'connect';
    case 'launch':
      return 'subscriptions';
  }
}

function HappyLoaderContent({
  cleanSlateState,
}: {
  cleanSlateState: CleanSlateState;
}) {
  function getPreviewText({
    cleanSlateState,
  }: {
    cleanSlateState: CleanSlateState;
  }): string | undefined {
    switch (cleanSlateState.type) {
      case 'initial':
        return undefined;
      case 'complete':
        return 'Finished building your app';
      case 'loading':
        if (cleanSlateState.showingLivePreview) {
          return undefined;
        }
        if (cleanSlateState.preparingLivePreview) {
          return 'Preparing your app';
        }
        return 'Building your app';
    }
  }
  const previewText = useMemo(() => {
    return getPreviewText({ cleanSlateState });
  }, [cleanSlateState]);
  if (!previewText) {
    return null;
  }

  return (
    <Column style={{ width: '100%', alignItems: 'center', height: '100%' }}>
      <Spacer />
      <Column style={{ alignItems: 'center' }}>
        <svg
          width={SlateIconWidth}
          height={SlateIconWidth}
          xmlns="http://www.w3.org/2000/svg"
        >
          <image href={happy} width={SlateIconWidth} height={SlateIconWidth} />
        </svg>
        <Font kind="font-slate-message">
          <div style={{ padding: 16 }}>{previewText}</div>
        </Font>
      </Column>
      <Spacer />
    </Column>
  );
}

function CompletionIndicator({
  isLoading,
  cleanSlateState,
}: {
  isLoading: boolean;
  cleanSlateState: CleanSlateState;
}) {
  const { state } = useScreenPreview();
  const { state: componentState } = useComponentsPreview();

  const allScreens = useMemo(() => {
    return Object.values(state.screensByID);
  }, [state.screensByID]);

  // TODO: Intelligently pick which screen to preview
  const firstScreen =
    allScreens.find((s) => s.name === 'Tabs') ?? allScreens[0];
  const rootComponent = useMemo(() => {
    const rootComponentID = firstScreen?.rootComponentID;
    if (rootComponentID === undefined) {
      return undefined;
    }

    return componentState.componentsByID[rootComponentID];
  }, [firstScreen?.rootComponentID, componentState.componentsByID]);
  return (
    <LivePreview component={rootComponent} showPreviewCode={true}>
      {isLoading && <HappyLoaderContent cleanSlateState={cleanSlateState} />}
    </LivePreview>
  );
}

export function CleanSlate() {
  const [state, setState] = useState<CleanSlateState>({ type: 'initial' });
  const [availableFidgetModules, setAvailableFidgetModules] = useState<
    FidgetModule[]
  >([]);

  const { state: conversationState } = useConversation();

  const { dispatch: colorsDispatch } = useColorPreview();
  const { dispatch: typographyDispatch } = useTypographyPreview();

  const insertNewFidgetModules = useCallback(function (
    fidgetModules: FidgetModule[],
  ) {
    setAvailableFidgetModules((availableFidgetModules) => {
      let updatedModules = availableFidgetModules;

      for (const fidgetModule of fidgetModules) {
        if (updatedModules.includes(fidgetModule)) {
          continue;
        }
        updatedModules = updatedModules.concat(fidgetModule);
      }
      return updatedModules;
    });
  }, []);

  useEffect(
    // Adds available fidget modules to the screen when they're ready
    function insertNewlyAvailableFidgetModules() {
      function fidgetModules(): FidgetModule[] {
        switch (state.type) {
          case 'initial':
            return [];
          case 'loading':
            return (
              conversationState.loading?.streaming?.availableFidgetModules ?? []
            );
          case 'complete':
            return ALL_FIDGET_MODULES;
        }
      }
      const activeFidgetModule = fidgetModules();
      insertNewFidgetModules(activeFidgetModule);
    },
    [state, conversationState.loading?.streaming, insertNewFidgetModules],
  );

  useEffect(
    // Changes state of LivePreview based on what modules are ready
    function updateStateBasedOnAvailableFidgets() {
      setState((currentState) => {
        if (currentState.type !== 'loading') {
          return currentState;
        }

        if (
          availableFidgetModules.includes('screens') &&
          currentState.showingLivePreview !== true
        ) {
          return {
            ...currentState,
            showingLivePreview: true,
          };
        }

        if (
          availableFidgetModules.includes('typography') &&
          currentState.preparingLivePreview !== true
        ) {
          return {
            ...currentState,
            preparingLivePreview: true,
          };
        }

        return currentState;
      });
    },
    [availableFidgetModules],
  );

  const { state: windowState, dispatch: _windowDispatch } = useWindows();

  const livePreviewWidth = useMemo(() => {
    return windowState.windows['live-preview'].size?.width;
  }, [windowState]);

  const {
    widthForFidgetModule,
    codiModuleWidth,
  }: { widthForFidgetModule?: number; codiModuleWidth?: number } =
    useMemo(() => {
      // Shut the linter up - we want to recalculate whenver windowState.resolutionCount changes
      const _ = windowState.resolutionCount;
      if (livePreviewWidth === undefined) {
        return { widthForFidgetModule: undefined, codiModuleWidth: undefined };
      }

      const fullWidth = window.innerWidth;
      const widthSansLivePreview = fullWidth - (livePreviewWidth + 32);
      const widthForFidgetModule =
        widthSansLivePreview / 2 - (176 + 30 + 8) - 40;

      return {
        widthForFidgetModule:
          widthForFidgetModule < 120 ? 120 : widthForFidgetModule,
        codiModuleWidth: widthSansLivePreview / 2 - (40 + 8 + 30),
      };
    }, [windowState.resolutionCount, livePreviewWidth]);

  const windowDispatch = useCallback(
    function windowDispatch(action: WindowAction) {
      console.log('[Window Dispatch]: ', action);
      _windowDispatch(action);
    },
    [_windowDispatch],
  );

  const isConversationLoading = conversationState.loading !== undefined;
  const hasLoadedConversation =
    conversationState.type === 'conversation' &&
    conversationState.loading === undefined;
  const hasExistingConversation =
    hasLoadedConversation && conversationState.items.length > 0;
  const hasResetConversation =
    conversationState.type === 'new' && conversationState.loading === undefined;

  useEffect(() => {
    // Controls state between CodiChat and all Modules + LivePreview.
    setState((currentState) => {
      if (currentState.type === 'loading' && hasResetConversation) {
        return { type: 'initial' };
      }

      if (currentState.type === 'initial' && isConversationLoading) {
        return { type: 'loading' };
      }

      if (currentState.type === 'loading' && hasLoadedConversation) {
        return { type: 'complete' };
      }

      // Handle case where an existing conversation is loaded (e.g., from JSON import)
      // without going through the loading state.
      if (currentState.type === 'initial' && hasExistingConversation) {
        return { type: 'complete' };
      }

      return currentState;
    });
  }, [
    hasExistingConversation,
    hasLoadedConversation,
    hasResetConversation,
    isConversationLoading,
  ]);

  useEffect(() => {
    // Window management based on states: initial, loading, complete
    switch (state.type) {
      case 'initial':
        windowDispatch({ type: 'hide-all', except: ['codi-tip'] });
        windowDispatch({ type: 'show', id: 'codi' });
        windowDispatch({
          type: 'setSize',
          size: { width: 480, height: 155 },
          id: 'codi',
        });
        windowDispatch({
          type: 'setPosition',
          position: { type: 'relative', x: 'center', y: 'center', yOffset: 54 },
          id: 'codi',
        });
        break;
      case 'loading':
        windowDispatch({
          type: 'setPosition',
          position: { type: 'after', otherID: 'live-preview' },
          id: 'codi',
        });
        windowDispatch({
          type: 'setSize',
          size: { width: codiModuleWidth ?? 480 },
          id: 'codi',
        });
        break;
      case 'complete':
        windowDispatch({ type: 'show', id: 'json-import' });
        windowDispatch({ type: 'show', id: 'component-preview-debug' });
        // TODO: Render a complete state
        break;
    }
  }, [state.type, windowDispatch, codiModuleWidth]);

  const [activeFidgetModuleIndex, _setActiveFidgetModuleIndex] =
    useState<number>(0);

  type SlateLayoutMode = 'full' | 'live-preview';
  const layoutMode: SlateLayoutMode =
    state.type === 'complete' ||
    (state.type === 'loading' &&
      (state.preparingLivePreview === true ||
        state.showingLivePreview === true))
      ? 'live-preview'
      : 'full';
  useEffect(
    function resetTransitionToFidgetRefForLayoutMode() {
      transitionedToFidgetRef.current = false;
    },
    [layoutMode],
  );

  const transitionedToFidgetRef = useRef(false);
  function setActiveFidgetModuleIndex(nextActiveFidgetModuleIndex: number) {
    if (nextActiveFidgetModuleIndex !== activeFidgetModuleIndex) {
      transitionedToFidgetRef.current = false;
    }
    _setActiveFidgetModuleIndex(nextActiveFidgetModuleIndex);
  }

  useEffect(() => {
    if (widthForFidgetModule === undefined) {
      return;
    }

    const activeFidgetModule = availableFidgetModules[activeFidgetModuleIndex];
    if (activeFidgetModule === undefined) {
      return;
    }

    const fidgetWindow = WindowIDForFidgetModule(activeFidgetModule);
    windowDispatch({
      type: 'setSize',
      id: fidgetWindow,
      size: {
        width: widthForFidgetModule,
      },
    });
  }, [
    widthForFidgetModule,
    activeFidgetModuleIndex,
    availableFidgetModules,
    windowDispatch,
  ]);

  useEffect(
    function transitionToFidget() {
      if (transitionedToFidgetRef.current) {
        return;
      }

      const activeFidgetModule =
        availableFidgetModules[activeFidgetModuleIndex];
      console.log(
        '[CLEAN SLATE] Transitioning to fidget: ',
        activeFidgetModule,
      );

      if (activeFidgetModule === undefined) {
        return;
      }
      transitionedToFidgetRef.current = true;
      const activeWindowID = WindowIDForFidgetModule(activeFidgetModule);

      if (activeWindowID === 'colors') {
        colorsDispatch({ type: 'setIsInSlate', isInSlate: true });
      } else {
        colorsDispatch({ type: 'setIsInSlate', isInSlate: false });
      }

      if (activeWindowID === 'typography') {
        typographyDispatch({ type: 'setIsInSlate', isInSlate: true });
      } else {
        typographyDispatch({ type: 'setIsInSlate', isInSlate: false });
      }

      windowDispatch({
        type: 'hide-all',
        except: [activeWindowID, 'codi-tip'],
      });
      windowDispatch({ type: 'mark-fixed', id: activeWindowID });

      windowDispatch({
        type: 'mark-fixed',
        id: 'codi',
      });

      windowDispatch({
        type: 'setPosition',
        id: activeWindowID,
        position: { type: 'before', otherID: 'live-preview' },
      });
      windowDispatch({
        type: 'setPosition',
        id: 'codi',
        position: { type: 'after', otherID: 'live-preview' },
      });

      windowDispatch({ type: 'show', id: 'codi' });
    },
    [
      availableFidgetModules,
      activeFidgetModuleIndex,
      windowDispatch,
      colorsDispatch,
      typographyDispatch,
    ],
  );

  if (!EnableCleanSlate) {
    return;
  }

  const showLivePreview =
    (state.type === 'loading' && state.showingLivePreview === true) ||
    state.type === 'complete';

  return (
    <div>
      <CodiTipPalette
        activeFidgetModuleIndex={activeFidgetModuleIndex}
        fidgetModules={availableFidgetModules}
        onFidgetClick={setActiveFidgetModuleIndex}
      />

      {state.type === 'initial' && (
        <div
          className="header-glow"
          style={{
            position: 'absolute',
            top: 'calc(50% - 60px)', // Vertical offset from center
            left: '50%',
            transform: 'translateX(-50%)',
            zIndex: 10,
            textAlign: 'center',
            pointerEvents: 'none',
            whiteSpace: 'nowrap',
          }}
        >
          <Font kind="font-landing-header">
            What would you like to codify today?
          </Font>
        </div>
      )}

      <Row
        style={{
          position: 'absolute',
          borderRadius: 12,
          left: '50%',
          top: '50%',
          transform: 'translate(-50%, -50%)',
          //...cssVariableSubstitutions,
        }}
      >
        {state.type !== 'initial' && (
          <CompletionIndicator
            isLoading={!showLivePreview}
            cleanSlateState={state}
          />
        )}
      </Row>
    </div>
  );
}
